<template>
  <JoszakiPage inner-class="flex flex-col gap-6">
    <BreadCrumbs :links="breadCrumbs" />
    <div class="flex flex-col gap-4">
      <JoszakiHeading class="text-center">
        {{ $t("professionTaskPage.hub.heading", { task: taskName }) }}
      </JoszakiHeading>
      <p class="text-lg">
        {{ $t("professionTaskPage.hub.lead", { task: taskWord }) }}
      </p>
      <QuickOffer
        @requestTender="onRequestTender({ source: 'quickOffer', text: $event })"
      />
    </div>

    <section v-if="page.cities.length" id="varosok" class="flex flex-col gap-3">
      <JoszakiHeading :level="2">
        {{ $t("professionTaskPage.hub.citiesHeading", { task: taskName }) }}
      </JoszakiHeading>
      <ul class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
        <li v-for="item in page.cities" :key="item.city.id">
          <NuxtLink class="text-primary hover:underline" :to="cityPath(item.city)">
            {{
              $t("professionTaskPage.hub.cityLink", {
                cityName: item.city.name,
                count: item.professionalCount,
              })
            }}
          </NuxtLink>
        </li>
      </ul>
    </section>

    <section v-if="people.length" class="flex flex-col gap-3">
      <JoszakiHeading :level="2">
        {{ $t("professionTaskPage.hub.peopleHeading", { task: taskWord }) }}
      </JoszakiHeading>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CardPerson
          v-for="person in people"
          :key="person.id"
          class="h-full"
          :highlighted-profession-task="page.professionTask"
          :person="person"
          show-go-to-house-km
          show-active-indicator
          @select="openProfile(person)"
          @callProfessional="onCallProfessional(person)"
          @requestTender="onRequestTender({ person, source: 'personCard' })"
        />
      </div>
    </section>

    <BlockTender
      text-input
      @requestTender="onRequestTender({ source: 'banner-block', text: $event })"
    />

    <div
      v-if="customContent"
      class="flex flex-col-reverse lg:grid lg:grid-cols-[3fr_1fr] gap-3"
    >
      <JoszakiHtmlTextBlock :text="customContent" />
      <div>
        <TableOfContents class="lg:sticky lg:top-3" :content="customContent" />
      </div>
    </div>
  </JoszakiPage>
</template>

<script>
import {
  capitalizeFirst,
  hubStatValues,
  lowerFirst,
  replaceTaskPagePlaceholders,
} from "./professionTaskCityContent";
import { professionTaskPagePath } from "./professionTaskPageOutcome";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import QuickOffer from "~/components/_refactored/app/boxes/QuickOffer.vue";
import TableOfContents from "~/components/_refactored/app/boxes/TableOfContents.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import JoszakiHtmlTextBlock from "~/components/_refactored/common/JoszakiHtmlTextBlock.vue";
import CardPerson from "~/components/people/CardPerson.vue";
import { useRequestTender } from "~/composables/requestTender";
import {
  useTrackFrontendEvent,
  FRONTEND_EVENT_TYPE,
  FRONTEND_EVENT_PLACEMENT,
} from "~/composables/tracking/useTrackFrontendEvent";

export default {
  name: "ProfessionTaskHubPage",
  components: {
    BlockTender,
    BreadCrumbs,
    CardPerson,
    JoszakiHtmlTextBlock,
    QuickOffer,
    TableOfContents,
  },
  props: {
    page: {
      type: Object,
      required: true,
    },
    people: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { trackFrontendEvent } = useTrackFrontendEvent();
    return {
      ...useRequestTender(),
      trackFrontendEvent,
    };
  },
  head() {
    const title = capitalizeFirst(
      this.replacePlaceholders(this.page.metaTitle) ||
        this.$t("seo.professionTaskHubPage.title", { taskName: this.taskName })
    );
    const description =
      this.replacePlaceholders(this.page.metaDescription) ||
      this.$t("seo.professionTaskHubPage.description", {
        task: this.taskWord,
        cityCount: this.page.cities.length,
      });

    return {
      title,
      meta: [
        { hid: "og:title", property: "og:title", content: title },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
        },
        { hid: "og:image", property: "og:image", content: "/joszaki-head.png" },
        { hid: "description", name: "description", content: description },
        {
          hid: "og:description",
          property: "og:description",
          content: description,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${this.$helpers.topDomain}${this.$route.path}`,
        },
      ],
    };
  },
  computed: {
    taskName() {
      return capitalizeFirst(this.page.professionTask.name);
    },
    taskWord() {
      return lowerFirst(this.page.professionTask.name);
    },
    customContent() {
      return replaceTaskPagePlaceholders(this.page.contentHtml, {
        taskName: this.page.professionTask.name,
        cityName: "",
        professionName: this.page.profession.name,
        statValues: hubStatValues(this.page.stats, {
          formatNumber: (value) => this.$n(value),
          formatRating: (value) => this.$n(value, "rating"),
        }),
        domParser: this.$domParser,
      });
    },
    breadCrumbs() {
      const { profession, professionTask } = this.page;
      const links = [];
      if (profession.professionGroup) {
        links.push({
          name: profession.professionGroup.name,
          page: this.localePath({
            name: "professionsLandingPage",
            hash: `#${profession.professionGroup.seoName}`,
          }),
        });
      }
      links.push({
        name: profession.plural,
        page: this.localePath({
          name: "professionPage",
          params: { profession: profession.seoName },
        }),
      });
      links.push({ name: professionTask.name, link: "" });
      return links;
    },
  },
  mounted() {
    this.$trackers.pageview({
      pagename: this.$trackers.pageNames.PROFESSION_TASK_PAGE,
      professionTaskSeoName: this.page.professionTask.seoName,
      professionSeoName: this.page.profession.seoName,
    });
  },
  beforeDestroy() {
    this.$trackers.pageleave({
      pagename: this.$trackers.pageNames.PROFESSION_TASK_PAGE,
    });
  },
  methods: {
    replacePlaceholders(text) {
      return replaceTaskPagePlaceholders(text, {
        taskName: this.page.professionTask.name,
        cityName: "",
        professionName: this.page.profession.name,
      });
    },
    cityPath(city) {
      return professionTaskPagePath(this.localePath, {
        slug: this.page.slug,
        citySeoName: city.seoName,
      });
    },
    openProfile(person) {
      const basePath = this.localePath({
        name: "professionalPage",
        params: { professionalSeoName: person.seoName },
      });
      window.open(
        `${basePath}#professionTask=${this.page.professionTask.seoName}`
      );
    },
    onCallProfessional(person) {
      this.trackFrontendEvent({
        eventType: FRONTEND_EVENT_TYPE.PHONE_CLICK,
        personId: person.id,
        placement: FRONTEND_EVENT_PLACEMENT.PERSON_CARD,
        metadata: {
          professionTaskSeoName: this.page.professionTask.seoName,
          professionSeoName: this.page.profession.seoName,
        },
      });
      this.openProfile(person);
    },
    onRequestTender({ person, source, text }) {
      this.requestTender({
        person,
        profession: this.page.profession,
        source,
        description: text,
      });
    },
  },
};
</script>
